// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import orderBy from 'lodash/orderBy'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'wouter'

import type { VoucherValueDecisionSummaryWithPermittedActions } from 'lib-common/generated/api-types/invoicing'
import type { PersonId } from 'lib-common/generated/api-types/shared'
import type LocalDate from 'lib-common/local-date'
import { formatCents } from 'lib-common/money'
import { cancelMutation, useQueryResult } from 'lib-common/query'
import { AddButtonRow } from 'lib-components/atoms/buttons/AddButton'
import { MutateButton } from 'lib-components/atoms/buttons/MutateButton'
import { Table, Tbody, Td, Th, Thead, Tr } from 'lib-components/layout/Table'
import { FixedSpaceRow } from 'lib-components/layout/flex-helpers'
import { PersonName } from 'lib-components/molecules/PersonNames'
import DatePicker from 'lib-components/molecules/date-picker/DatePicker'
import { MutateFormModal } from 'lib-components/molecules/modals/FormModal'
import { Label } from 'lib-components/typography'
import { defaultMargins } from 'lib-components/white-space'
import { featureFlags } from 'lib-customizations/employee'
import { faPlus } from 'lib-icons'
import { faBoxArchive } from 'lib-icons'

import { useTranslation } from '../../state/i18n'
import { UIContext } from '../../state/ui'
import { renderResult } from '../async-rendering'

import {
  generateRetroactiveVoucherValueDecisionsMutation,
  headOfFamilyVoucherValueDecisionsQuery,
  planArchiveVoucherValueDecisionMutation
} from './queries'
import { PersonContext } from './state'

interface Props {
  id: PersonId
}

export default React.memo(function PersonVoucherValueDecisions({ id }: Props) {
  const { i18n } = useTranslation()
  const { uiMode, toggleUiMode, clearUiMode } = useContext(UIContext)
  const { permittedActions } = useContext(PersonContext)
  const voucherValueDecisions = useQueryResult(
    headOfFamilyVoucherValueDecisionsQuery({ headOfFamilyId: id })
  )

  const openRetroactiveDecisionsModal = useCallback(
    () => toggleUiMode('create-retroactive-value-decisions'),
    [toggleUiMode]
  )

  return (
    <>
      {uiMode === 'create-retroactive-value-decisions' ? (
        <Modal headOfFamily={id} clear={clearUiMode} />
      ) : null}
      {permittedActions.has('GENERATE_RETROACTIVE_VOUCHER_VALUE_DECISIONS') && (
        <AddButtonRow
          data-qa="create-retroactive-value-decisions"
          text={i18n.personProfile.voucherValueDecisions.createRetroactive}
          onClick={openRetroactiveDecisionsModal}
          disabled={!!uiMode}
        />
      )}
      {renderResult(voucherValueDecisions, (voucherValueDecisions) => (
        <PersonVoucherValueDecisionsTable
          voucherValueDecisions={voucherValueDecisions}
        />
      ))}
    </>
  )
})

const PersonVoucherValueDecisionsTable = ({
  voucherValueDecisions
}: {
  voucherValueDecisions: VoucherValueDecisionSummaryWithPermittedActions[]
}) => {
  const { i18n } = useTranslation()
  const archivedColumnVisible = useMemo(
    () =>
      featureFlags.archiveIntegration?.voucherValueDecisions &&
      voucherValueDecisions.some(({ permittedActions }) =>
        permittedActions.includes('ARCHIVE')
      ),
    [voucherValueDecisions]
  )

  return (
    <Table data-qa="table-of-voucher-value-decisions">
      <Thead>
        <Tr>
          <Th>{i18n.valueDecisions.table.validity}</Th>
          <Th>{i18n.valueDecisions.table.number}</Th>
          <Th>{i18n.valueDecisions.table.totalValue}</Th>
          <Th>{i18n.valueDecisions.table.totalCoPayment}</Th>
          <Th>{i18n.valueDecisions.table.createdAt}</Th>
          <Th>{i18n.valueDecisions.table.sentAt}</Th>
          <Th>{i18n.valueDecisions.table.status}</Th>
          {archivedColumnVisible && (
            <Th>{i18n.personProfile.decision.archived}</Th>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {orderBy(
          voucherValueDecisions,
          [
            ({ data: decision }) => decision.validFrom,
            ({ data: decision }) => decision.sentAt
          ],
          ['desc', 'desc']
        ).map(({ data: decision, permittedActions }) => {
          const formattedRange = `${decision.validFrom.format()} - ${
            decision.validTo?.format() ?? ''
          }`

          return (
            <Tr key={decision.id} data-qa="table-voucher-value-decision-row">
              <Td>
                <PersonName person={decision.child} format="Last First" />
                <br />
                {decision.annullingDecision ? (
                  <span>
                    {i18n.valueDecisions.table.annullingDecision}{' '}
                    {formattedRange}
                  </span>
                ) : (
                  <Link to={`/finance/value-decisions/${decision.id}`}>
                    {formattedRange}
                  </Link>
                )}
              </Td>
              <Td>{decision.decisionNumber}</Td>
              <Td>{formatCents(decision.voucherValue)}</Td>
              <Td>{formatCents(decision.finalCoPayment)}</Td>
              <Td>{decision.created.toLocalDate().format()}</Td>
              <Td data-qa="voucher-value-decision-sent-at">
                {decision.sentAt?.toLocalDate().format() ?? ''}
              </Td>
              <Td>{i18n.valueDecision.status[decision.status]}</Td>
              {featureFlags.archiveIntegration?.voucherValueDecisions &&
                permittedActions.includes('ARCHIVE') && (
                  <Td>
                    {decision.archivedAt !== null ? (
                      decision.archivedAt.toLocalDate().format()
                    ) : (
                      <MutateButton
                        icon={faBoxArchive}
                        text={i18n.personProfile.decision.archive}
                        mutation={planArchiveVoucherValueDecisionMutation}
                        onClick={() => ({ id: decision.id })}
                        data-qa="archive-button"
                        disabled={
                          decision.status !== 'SENT' &&
                          decision.status !== 'ANNULLED'
                        }
                      />
                    )}
                  </Td>
                )}
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

const StyledLabel = styled(Label)`
  margin-top: ${defaultMargins.xs};
`

const Modal = React.memo(function Modal({
  headOfFamily,
  clear
}: {
  headOfFamily: PersonId
  clear: () => void
}) {
  const { i18n, lang } = useTranslation()
  const [date, setDate] = useState<LocalDate | null>(null)

  return (
    <MutateFormModal
      icon={faPlus}
      type="info"
      title={i18n.personProfile.voucherValueDecisions.createRetroactive}
      resolveMutation={generateRetroactiveVoucherValueDecisionsMutation}
      resolveAction={() =>
        date !== null
          ? {
              id: headOfFamily,
              body: { from: date }
            }
          : cancelMutation
      }
      resolveLabel={i18n.common.create}
      resolveDisabled={!date}
      onSuccess={clear}
      rejectAction={clear}
      rejectLabel={i18n.common.cancel}
    >
      <FixedSpaceRow $justifyContent="center">
        <StyledLabel>{i18n.common.form.startDate}</StyledLabel>
        <DatePicker
          data-qa="retroactive-value-decisions-from-date"
          locale={lang}
          date={date}
          onChange={setDate}
          hideErrorsBeforeTouched
        />
      </FixedSpaceRow>
    </MutateFormModal>
  )
})
